import { type Dispatch, type FC, useEffect } from "react";

import { type ApiServiceDescriptor, CloudflowEngine } from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { type TransformMethod, useCollectionDataOnce } from "@doitintl/models-hooks";
import { Autocomplete, Box, Divider, FormControl, TextField, Typography } from "@mui/material";

import { useErrorSnackbar } from "../../../Integrations/Slack/utils";
import { type ProviderFilter, type ServiceFilter } from "./hooks/useActionSearchDialogStateMachine";

const noServicesFoundOption: ServiceFilter = { id: "no-results-found", name: "No results found", version: "" };

const transformServices: TransformMethod<ApiServiceDescriptor, ServiceFilter> = (
  { serviceName, latestVersion },
  { id }
) => ({
  id,
  name: serviceName,
  version: latestVersion,
});

export const ActionSearchServiceFilter: FC<{
  provider: ProviderFilter;
  value: ServiceFilter | undefined;
  onChange: Dispatch<ServiceFilter | undefined>;
}> = ({ provider, value, onChange }) => {
  const showError = useErrorSnackbar();
  const [services, loadingServices, error] = useCollectionDataOnce(
    getCollection(CloudflowEngine)
      .doc("integrations")
      .collection("apis")
      .doc(provider.id)
      .collection("services")
      .orderBy("serviceName"),
    {
      transform: transformServices,
      caching: true,
      cachingKeys: ["cloudflow-services", provider.id],
    }
  );

  useEffect(() => {
    if (error) {
      showError(error.message);
    }
  }, [error, showError]);

  const allServicesOption: ServiceFilter = { id: "", name: `All ${provider.name} services`, version: "" };

  const selectedOption = value ?? allServicesOption;

  const options = [allServicesOption, ...(services ?? [])];

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 250,
        backgroundColor: "general.backgroundDark",
        "& .MuiOutlinedInput-root": {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          "& fieldset": {
            borderLeft: "none",
          },
          "&.Mui-focused fieldset": {
            border: "2px solid",
            borderColor: "primary.main",
          },
          "&:hover fieldset": {
            border: "1px solid",
          },
        },
      }}
    >
      <Autocomplete
        autoSelect
        openOnFocus
        value={selectedOption}
        onChange={({ type }, newValue, reason) => {
          if (type !== "change" || reason !== "clear") {
            onChange(newValue === null || newValue.id === allServicesOption.id ? undefined : newValue);
          }
        }}
        options={options}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={loadingServices}
        filterOptions={(options, { inputValue }) => {
          const searchLower = inputValue.trim().toLowerCase();
          const filteredOptions = options.filter(
            (service) =>
              service.id === allServicesOption.id ||
              service.name?.toLowerCase().includes(searchLower) ||
              service.id.toLowerCase().includes(searchLower)
          );
          if (filteredOptions.length === 1) {
            filteredOptions.push(noServicesFoundOption);
          }
          return filteredOptions;
        }}
        getOptionDisabled={(option) => option.id === noServicesFoundOption.id}
        renderInput={(params) => <TextField {...params} placeholder="Filter by service" size="small" />}
        renderOption={(props, option) => (
          <>
            <Box
              {...props}
              component="li"
              title={option.name}
              key={option.id}
              sx={{
                display: "block !important",
              }}
            >
              <Typography
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {option.name}
              </Typography>
              {option.version && (
                <Typography variant="caption" color="text.secondary">
                  Version: {option.version}
                </Typography>
              )}
            </Box>
            {option.id === allServicesOption.id && <Divider key="divider-component" sx={{ my: 1 }} />}
          </>
        )}
        slotProps={{
          popper: {
            sx: {
              width: "300px !important",
            },
          },
        }}
      />
    </FormControl>
  );
};
